import { memo, useEffect } from "react"
import { ScrollArea } from "../../components/shadcn/scroll-area"

import ModalHeader from "./alertModalComponenets/ModalHeader"
import ModalFooter from "./alertModalComponenets/ModalFooter"
import LoadingState from "./alertModalComponenets/LoadingState"
import ErrorState from "./alertModalComponenets/ErrorState"
import EmptyState from "./alertModalComponenets/EmptyState"
import ProductItem from "./alertModalComponenets/ProductItem"
import { Product } from "@/src/types/Product";


// Interface for props
interface AlertModalProps {
  isVisible: boolean
  onClose: () => void
  products: Product[]
  isLoading?: boolean
  error?: string | null
}

// Main notification modal component
const AlertModal = memo(({ isVisible, onClose, products = [], isLoading = false, error = null }: AlertModalProps) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isVisible) {
        onClose()
      }
    }

    window.addEventListener("keydown", handleEscape)

    // Prevent scrolling when modal is open
    if (isVisible) {
      document.body.style.overflow = "hidden"
    }

    return () => {
      window.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [isVisible, onClose])

  // Early return if not visible
  if (!isVisible) {
    return null
  }

  // Make sure products is an array
  const safeProducts = Array.isArray(products) ? products : []
  const totalLowStockItems = safeProducts.length

  // Modal content with fixed positioning for browser center
  return (
    <>
      {/* Portal container that's outside of any parent component flow */}
      <div
        id="modal-portal"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        {/* Dark overlay */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)'
          }}
          onClick={onClose}
        />

        {/* Modal container - absolutely positioned in the center */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '85vh',
            margin: '0 auto',
            zIndex: 10000,
            animation: 'fadeIn 0.3s, scaleIn 0.3s',
            backgroundColor: 'var(--modal-bg, white)',
            borderRadius: '0.75rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            border: '1px solid var(--modal-border, rgba(0, 0, 0, 0.1))',
            overflowY: 'scroll',
          }}
          className="dark:bg-neutral-900 dark:border-neutral-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal header */}
          <ModalHeader title="Alerte de Stock Faible" onClose={onClose} />

          {/* Status message */}
          <div className="px-4 sm:px-6 py-3 border-b dark:border-neutral-700 bg-muted/30 dark:bg-neutral-800/50">
            <p className="text-muted-foreground dark:text-neutral-400">
              {isLoading
                ? "Chargement des données d'inventaire..."
                : error
                  ? error
                  : `${totalLowStockItems} ${totalLowStockItems === 1 ? 'produit est' : 'produits sont'} en dessous de leur seuil de stock minimum.`}
            </p>
          </div>

          {/* Modal content based on state */}
          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState error={error} onClose={onClose} />
          ) : (
            <ScrollArea className="flex-grow p-4 dark:bg-neutral-900" style={{ maxHeight: "60vh" }}>
              <div className="space-y-3">
                {safeProducts.length === 0 ? (
                  <EmptyState />
                ) : (
                  safeProducts.map((product) => (
                    <ProductItem
                      key={product.id}
                      product={product}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          )}

          {/* Modal footer */}
          <ModalFooter
            onClose={onClose}
            showGenerateReport={!isLoading && !error && safeProducts.length > 0}
            hasError={!!error}
          />
        </div>
      </div>

      {/* Add keyframe animations and CSS variables for dark mode */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); }
          to { transform: scale(1); }
        }
        
        .dark {
          --modal-bg: #171717;
          --modal-border: rgba(82, 82, 82, 0.4);
        }
      `}</style>
    </>
  )
})

export default AlertModal